import * as React from 'react';
import { routingEnabled } from 'utils/ui/app';
import MultiVoipWithRouting from './MultiVoipWithRouting';
import MultiVoipInternal from './MultiVoipInternal';

/**
 * Snapshot of the "most important" remote participant for any consumer that needs to
 * render a single tile (e.g. the collapsed pill in {@link VoipPanel}).
 *
 * `null` means exactly one thing: NO remote is connected (AVS-4275's "alone ⇒ single You tile").
 * AVS-4363: a connected participant that publishes no video — camera denied, so the tab
 * negotiated receive-only, or a master whose stream has not arrived yet — is reported with
 * `stream: null` instead of collapsing to `null`, so consumers keep its tile and render their
 * placeholder. Conflating the two made a camera-less counterpart disappear from the pill.
 */
export type ActiveVoipParticipant = {
  stream: MediaStream | null;
  audioMuted: boolean;
  videoMuted: boolean;
} | null;

export type MultiVoipProps = {
  controls?: boolean;
  className?: string;
  /**
   * Bubbled from MultiVoipInternal so View.tsx can drive the collapsed-pill pulse without
   * mounting `useVoipConnections` twice (the hook drives `voipRtcConnection.publish()`).
   * Callers MUST pass a stable reference (e.g. `useCallback`) to avoid spurious re-emissions.
   */
  onActiveSpeakerChange?: (isActiveSpeaker: boolean) => void;
  /**
   * Bubbled from MultiVoipInternal so VoipPanel can render the active-speaker tile inside
   * the collapsed pill. Same single-mount constraint as `onActiveSpeakerChange`.
   * Callers MUST pass a stable reference (e.g. `useCallback`) — the underlying `useEffect`
   * lists this callback in its deps, so a fresh function on every render would re-fire the
   * emit even when the participant data has not changed.
   */
  onActiveParticipantChange?: (participant: ActiveVoipParticipant) => void;
  /**
   * Bubbled from MultiVoipInternal so the collapsed pill can mirror a STANDBY dealer's own
   * face (the master tab's loopback) in its "You" tile — a standby has no `myCameraStream`,
   * so without this the minimized pill would only ever show the person-avatar placeholder.
   * `null` for a master / customer / flag-off tab, or before the loopback arrives. Same
   * single-mount constraint as the other callbacks — pass a stable reference.
   */
  onSelfMirrorChange?: (mirror: ActiveVoipParticipant) => void;
  /**
   * Slotted into the controls bar after `<EndSessionButton />` so VoipPanel can place
   * the minimize toggle as a regular flex sibling next to end-call instead of as an
   * absolute overlay (AVS-4218).
   */
  extraControls?: React.ReactNode;
  /**
   * Caps the total number of video tiles (self + remotes) rendered inside the
   * expanded panel. `undefined` means uncapped on desktop and auto-capped to 2
   * on phone viewports (≤629px). When set, the caller's value always wins.
   */
  maxParticipants?: number;
};

const MultiVoip: React.FC<MultiVoipProps> = ({
  controls,
  className,
  onActiveSpeakerChange,
  onActiveParticipantChange,
  onSelfMirrorChange,
  extraControls,
  maxParticipants,
}) => {
  return routingEnabled() ? (
    <MultiVoipWithRouting
      controls={controls}
      className={className}
      onActiveSpeakerChange={onActiveSpeakerChange}
      onActiveParticipantChange={onActiveParticipantChange}
      onSelfMirrorChange={onSelfMirrorChange}
      extraControls={extraControls}
      maxParticipants={maxParticipants}
    />
  ) : (
    <MultiVoipInternal
      controls={controls}
      className={className}
      onActiveSpeakerChange={onActiveSpeakerChange}
      onActiveParticipantChange={onActiveParticipantChange}
      onSelfMirrorChange={onSelfMirrorChange}
      extraControls={extraControls}
      maxParticipants={maxParticipants}
    />
  );
};

export default MultiVoip;
